/* eslint-disable no-restricted-globals */
/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useContext, useEffect, useState } from 'react';
import { Badge, Form, Card, Alert, Spinner, Button, Row, Col, Table, Modal } from 'react-bootstrap';
import Axios from 'axios';
import { CoreContext } from '../context/core-context';
import { AuthContext } from '../context/auth-context';
import { useHistory, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { useRazorpay } from "react-razorpay";

const PRESET_AMOUNTS = [500, 1000, 2100, 5000, 10000];

const ContributeInSahyogAPI = (props) => {
    const { Razorpay } = useRazorpay();
    const coreContext = useContext(CoreContext);
    const authContext = useContext(AuthContext);
    const history = useHistory();
    const location = useLocation();

    const eventId = props.match?.params?.id;

    // Event details state
    const [event, setEvent] = useState(location?.state?.event || null);
    const [isLoadingEvent, setIsLoadingEvent] = useState(!location?.state?.event);
    
    // Contributor & stats state
    const [contributors, setContributors] = useState([]);
    const [totalRaised, setTotalRaised] = useState(0);

    // Contribution form state
    const [amount, setAmount] = useState('1000');
    const [comment, setComment] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [feedbackType, setFeedbackType] = useState('info');

    // Existing user contribution status
    const [userContribution, setUserContribution] = useState(null);

    // Document preview modal
    const [showDocModal, setShowDocModal] = useState(false);

    // 1. Fetch Event Details
    const fetchEventDetails = () => {
        if (!eventId) return;

        // Check if already in context
        const cached = coreContext.sahyogEvents?.find(e => String(e.id) === String(eventId));
        if (cached && !event) {
            setEvent(cached);
            setIsLoadingEvent(false);
        }

        // Fetch from API to ensure freshest data
        Axios.get('/jnv-sahyog-events', { params: { ownerId: authContext.ownerId } })
            .then(res => {
                if (res.data && res.data.events) {
                    const found = res.data.events.find(e => String(e.id) === String(eventId));
                    if (found) {
                        setEvent(found);
                    }
                }
                setIsLoadingEvent(false);
            })
            .catch(err => {
                console.error('Error fetching event details:', err);
                setIsLoadingEvent(false);
            });
    };

    // 2. Fetch Contributors & Total Raised
    const fetchContributors = () => {
        if (!eventId) return;

        Axios.get('/jnv-sahyog-events-contributors', { params: { eventId } })
            .then(res => {
                if (res.data && res.data.events) {
                    const uniqueEvents = [];
                    const seenIds = new Set();
                    let sum = 0;

                    res.data.events.forEach(item => {
                        if (!seenIds.has(item.id)) {
                            seenIds.add(item.id);
                            uniqueEvents.push(item);
                        }
                        if (item.amount) {
                            sum += parseFloat(item.amount) || 0;
                        }
                    });

                    setContributors(uniqueEvents);
                    setTotalRaised(sum);
                }
            })
            .catch(err => {
                console.error('Error fetching contributors:', err);
            });
    };

    // 3. Fetch current user's prior contribution status for this event
    const fetchUserContribution = () => {
        if (!eventId || !authContext.itemId) return;

        Axios.get('jnv-event-items', { params: { eventId, itemId: authContext.itemId } })
            .then(res => {
                if (res.data && res.data.event) {
                    setUserContribution(res.data.event);
                }
            })
            .catch(err => {
                console.error('Error checking user contribution status:', err);
            });
    };

    useEffect(() => {
        fetchEventDetails();
        fetchContributors();
        fetchUserContribution();
    }, [eventId, authContext.itemId]);

    // Handle Preset Amount Clicks
    const handlePresetClick = (preset) => {
        setAmount(String(preset));
        setFeedbackMessage('');
    };

    // Handle Custom Amount Change
    const handleAmountChange = (e) => {
        const val = e.target.value;
        setAmount(val);
        setFeedbackMessage('');
    };

    // Razorpay Flow
    const createOrder = () => {
        const parsedAmount = parseFloat(amount);
        if (!parsedAmount || parsedAmount <= 0) {
            setFeedbackMessage('Please enter a valid contribution amount.');
            setFeedbackType('warning');
            return;
        }

        setIsProcessing(true);
        setFeedbackMessage('Initiating secure payment order, please wait...');
        setFeedbackType('info');

        Axios.post('/create-order', {
            amount: parsedAmount,
            currency: 'INR',
            receipt: `sahyog_rcpt_${Date.now()}`,
            notes: { ptype: 'contribution', eventId, beneficiary: event?.item_name || 'Sahyog' }
        })
            .then(response => {
                if (response.data && response.data.id) {
                    handlePayment(response.data.id, parsedAmount);
                } else {
                    setIsProcessing(false);
                    setFeedbackMessage('Unable to initialize payment order. Please try again.');
                    setFeedbackType('danger');
                }
            })
            .catch(err => {
                setIsProcessing(false);
                console.error('Error generating order:', err);
                setFeedbackMessage('Failed to create payment order. Please try again later.');
                setFeedbackType('danger');
            });
    };

    const recordPayment = (paymentId, paidAmount) => {
        const paymentComment = comment.trim() || `Sahyog Contribution for ${event?.item_name || 'Sahyog Event'}`;

        Axios.post('/record-event-payment', {
            itemId: authContext.itemId,
            eventId: eventId,
            amount: paidAmount,
            paymentId,
            comment: paymentComment,
        })
            .then(response => {
                setIsProcessing(false);
                const successMsg = response.data.message || 'Thank you! Your contribution has been recorded successfully.';
                setFeedbackMessage(successMsg);
                setFeedbackType('success');
                if (toast) toast.success(successMsg);

                // Refresh contributors and user status
                fetchContributors();
                fetchUserContribution();
            })
            .catch(err => {
                setIsProcessing(false);
                console.error('Error recording payment:', err);
                const errDetail = err.response?.data?.data?.[0]?.msg || 'Payment successful, but error occurred while saving the record. Please contact support.';
                setFeedbackMessage(errDetail);
                setFeedbackType('danger');
            });
    };

    const handlePayment = (orderId, paidAmount) => {
        const donorName = coreContext.client?.item_name || "Navodaya Alumnus";
        const donorEmail = coreContext.client?.item_email || authContext.email || "swabhimanjnv@gmail.com";
        const donorPhone = coreContext.client?.item_contact || "9999999999";
        const beneficiaryName = event?.item_name || "Sahyog Beneficiary";

        const options = {
            key: "rzp_live_TZ8r4C3jFitOCQ",
            amount: paidAmount * 100, // paise
            currency: "INR",
            name: "Swabhiman - Being A Navodian",
            description: `Sahyog Contribution for ${beneficiaryName}`,
            order_id: orderId,
            handler: (response) => {
                setFeedbackMessage("Payment successful! Recording your contribution...");
                setFeedbackType('info');
                recordPayment(response.razorpay_payment_id, paidAmount);
            },
            prefill: {
                name: donorName,
                email: donorEmail,
                contact: donorPhone,
            },
            theme: {
                color: "#1e40af",
            },
            modal: {
                ondismiss: () => {
                    setIsProcessing(false);
                    setFeedbackMessage('Payment was cancelled.');
                    setFeedbackType('warning');
                }
            }
        };

        try {
            const razorpayInstance = new Razorpay(options);
            razorpayInstance.open();
        } catch (err) {
            console.error('Razorpay init error:', err);
            setIsProcessing(false);
            setFeedbackMessage('Error launching payment gateway. Please check your connection.');
            setFeedbackType('danger');
        }
    };

    // Helper for Document/Attachment URL
    const getDocUrl = () => {
        if (!event?.imageUrl) return null;
        return event.imageUrl.startsWith('http') ? event.imageUrl : `https://${event.imageUrl}`;
    };

    const docUrl = getDocUrl();
    const isDocPdf = docUrl ? docUrl.toLowerCase().includes('.pdf') : false;

    return (
        <div className="container py-4">
            {/* Top Navigation & Breadcrumb */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => history.push('/')}
                        className="me-2"
                    >
                        ← Back to Dashboard
                    </Button>
                    <span className="text-muted small">/ Sahyog Relief & Support</span>
                </div>
                <div>
                    <Badge bg="success" className="px-3 py-2 text-uppercase fs-7 shadow-sm">
                        🛡️ Verified Sahyog Initiative
                    </Badge>
                </div>
            </div>

            {isLoadingEvent ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" role="status" />
                    <p className="mt-3 text-muted">Loading Sahyog event details...</p>
                </div>
            ) : (
                <Row className="g-4">
                    {/* Left Column: Beneficiary & Event Details */}
                    <Col lg={7}>
                        {/* Beneficiary Profile Card */}
                        <Card className="shadow-sm border-0 mb-4 overflow-hidden">
                            <div className="p-4 text-white" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)' }}>
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center bg-white text-primary fw-bold shadow"
                                        style={{ width: '56px', height: '56px', fontSize: '1.4rem' }}
                                    >
                                        {event?.item_name ? event.item_name.charAt(0).toUpperCase() : 'S'}
                                    </div>
                                    <div>
                                        <div className="text-white-50 small text-uppercase fw-bold" style={{ letterSpacing: '1px' }}>
                                            Beneficiary / Member
                                        </div>
                                        <h3 className="mb-0 fw-bold">{event?.item_name || 'Navodaya Member'}</h3>
                                    </div>
                                </div>
                            </div>

                            <Card.Body className="p-4">
                                <Row className="g-3 mb-4">
                                    <Col sm={6}>
                                        <div className="p-3 bg-light rounded border h-100">
                                            <div className="text-muted small mb-1">📍 District / Region</div>
                                            <div className="fw-bold fs-6 text-dark">{event?.district || 'Not Specified'}</div>
                                        </div>
                                    </Col>
                                    <Col sm={6}>
                                        <div className="p-3 bg-light rounded border h-100">
                                            <div className="text-muted small mb-1">📅 Initiative / Joining Date</div>
                                            <div className="fw-bold fs-6 text-dark">{event?.membership_start_date || 'Active Member'}</div>
                                        </div>
                                    </Col>
                                    {event?.address && (
                                        <Col xs={12}>
                                            <div className="p-3 bg-light rounded border">
                                                <div className="text-muted small mb-1">🏠 Address / Location</div>
                                                <div className="fw-bold text-dark">{event.address}</div>
                                            </div>
                                        </Col>
                                    )}
                                </Row>

                                {/* Event Purpose / Story */}
                                <div className="mb-4">
                                    <h5 className="fw-bold text-dark mb-3">
                                        📝 Details & Purpose of Sahyog
                                    </h5>
                                    <div
                                        className="p-3 rounded border"
                                        style={{ backgroundColor: '#f8fafc', whiteSpace: 'pre-wrap', lineHeight: '1.7' }}
                                    >
                                        {event?.comments ? event.comments : 'Details and supporting information are listed above for this Sahyog cause.'}
                                    </div>
                                </div>

                                {/* Supporting Document / Attachment */}
                                <div>
                                    <h5 className="fw-bold text-dark mb-3">
                                        📎 Supporting Document / Attachment
                                    </h5>
                                    {docUrl ? (
                                        <div className="p-3 bg-light rounded border d-flex flex-wrap align-items-center justify-content-between gap-3">
                                            <div className="d-flex align-items-center gap-3">
                                                {isDocPdf ? (
                                                    <img
                                                        src="https://node-websites.siddhantait.com/images/pdfimage.png"
                                                        alt="PDF Document"
                                                        style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <img
                                                        src={docUrl}
                                                        alt="Supporting Document"
                                                        className="img-thumbnail"
                                                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                    />
                                                )}
                                                <div>
                                                    <div className="fw-bold text-dark">
                                                        {isDocPdf ? 'Verification PDF Document' : 'Supporting Certificate / Image'}
                                                    </div>
                                                    <span className="text-muted small">Click below to view full document</span>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <a
                                                    href={docUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-outline-primary btn-sm"
                                                >
                                                    View Full Attachment ↗
                                                </a>
                                                {!isDocPdf && (
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={() => setShowDocModal(true)}
                                                    >
                                                        Quick Preview
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-3 bg-light rounded border text-muted small">
                                            No additional attachments or certificates were uploaded for this event.
                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Community Support & Contributors Card */}
                        <Card className="shadow-sm border-0">
                            <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
                                <span className="fw-bold text-dark">🤝 Community Support & Contributors</span>
                                <Badge bg="primary" className="px-2 py-1">
                                    {contributors.length} {contributors.length === 1 ? 'Contributor' : 'Contributors'}
                                </Badge>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {totalRaised > 0 && (
                                    <div className="p-3 border-bottom bg-light d-flex justify-content-between align-items-center">
                                        <span className="text-muted">Total Funds Raised for this Cause:</span>
                                        <span className="fw-bold text-success fs-5">
                                            ₹{totalRaised.toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                )}
                                {contributors.length > 0 ? (
                                    <Table responsive hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Donor / Contributor</th>
                                                <th>District</th>
                                                <th>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {contributors.slice(0, 5).map((c, idx) => (
                                                <tr key={idx}>
                                                    <td>{idx + 1}</td>
                                                    <td className="fw-semibold">{c.item_name || 'Navodian Contributor'}</td>
                                                    <td>{c.district || '-'}</td>
                                                    <td className="text-success fw-bold">₹{parseFloat(c.amount || 0).toLocaleString('en-IN')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <div className="p-4 text-center text-muted small">
                                        Be the first to step forward and support this cause!
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Right Column: Contribution Checkout Box */}
                    <Col lg={5}>
                        <Card className="shadow border-0 sticky-top" style={{ top: '85px', zIndex: 10 }}>
                            <Card.Header className="py-3 text-white" style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' }}>
                                <div className="d-flex align-items-center gap-2">
                                    <span style={{ fontSize: '1.25rem' }}>❤️</span>
                                    <h5 className="mb-0 fw-bold">Make a Contribution</h5>
                                </div>
                            </Card.Header>

                            <Card.Body className="p-4">
                                {/* Previous Contribution Notification */}
                                {userContribution && userContribution.status === 'verified' && (
                                    <Alert variant="success" className="py-2 mb-3">
                                        <div className="fw-bold mb-1">✓ You have previously contributed!</div>
                                        <div className="small">
                                            Recorded Amount: <strong>₹{userContribution.amount}</strong>. You can make an additional contribution below anytime.
                                        </div>
                                    </Alert>
                                )}

                                {/* Feedback alerts */}
                                {feedbackMessage && (
                                    <Alert variant={feedbackType} className="py-2 mb-3">
                                        {feedbackMessage}
                                    </Alert>
                                )}

                                <Form onSubmit={(e) => { e.preventDefault(); createOrder(); }}>
                                    {/* Preset Amount Chips */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold small text-muted text-uppercase mb-2">
                                            Select Amount
                                        </Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {PRESET_AMOUNTS.map((amt) => {
                                                const isSelected = String(amt) === String(amount);
                                                return (
                                                    <Button
                                                        key={amt}
                                                        variant={isSelected ? "primary" : "outline-secondary"}
                                                        size="sm"
                                                        className="px-3 py-2 fw-bold"
                                                        style={{ borderRadius: '20px' }}
                                                        onClick={() => handlePresetClick(amt)}
                                                    >
                                                        ₹{amt.toLocaleString('en-IN')}
                                                    </Button>
                                                );
                                            })}
                                        </div>
                                    </Form.Group>

                                    {/* Custom Amount Input */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">
                                            Contribution Amount (₹ INR)
                                        </Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light fw-bold text-muted">₹</span>
                                            <Form.Control
                                                type="number"
                                                value={amount}
                                                onChange={handleAmountChange}
                                                placeholder="Enter custom amount"
                                                min="1"
                                                required
                                                className="fw-bold fs-5 text-primary"
                                            />
                                        </div>
                                        <Form.Text className="text-muted small">
                                            Every amount, big or small, makes a vital difference.
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Optional Note / Message */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold small text-muted text-uppercase">
                                            Words of Encouragement / Note (Optional)
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            placeholder="Write a warm note or blessing..."
                                        />
                                    </Form.Group>

                                    {/* Donor Info Summary */}
                                    <div className="p-3 mb-3 bg-light rounded border">
                                        <div className="text-muted small mb-1">Contributing As</div>
                                        <div className="fw-bold text-dark">
                                            {coreContext.client?.item_name || 'Navodaya Member'}
                                        </div>
                                        <div className="text-muted small">
                                            {coreContext.client?.item_email || authContext.email || 'Alumni Member'}
                                        </div>
                                    </div>

                                    {/* Submit / Pay CTA Button */}
                                    <div className="d-grid gap-2">
                                        <Button
                                            variant="success"
                                            size="lg"
                                            type="submit"
                                            disabled={isProcessing || !amount || parseFloat(amount) <= 0}
                                            className="fw-bold py-3 shadow"
                                        >
                                            {isProcessing ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Processing Secure Payment...
                                                </>
                                            ) : (
                                                `Contribute ₹${parseFloat(amount || 0).toLocaleString('en-IN')} Now`
                                            )}
                                        </Button>
                                    </div>

                                    {/* Trust & Security Badge */}
                                    <div className="text-center text-muted small mt-3">
                                        🔒 100% Secure Checkout via Razorpay (UPI, Cards, NetBanking)
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Quick Preview Modal for Document Image */}
            <Modal show={showDocModal} onHide={() => setShowDocModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6 fw-bold">Supporting Document / Certificate</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-3">
                    {docUrl && (
                        <img
                            src={docUrl}
                            alt="Full Supporting Document"
                            className="img-fluid rounded"
                            style={{ maxHeight: '75vh', objectFit: 'contain' }}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <a
                        href={docUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-sm me-auto"
                    >
                        Open Original in New Tab ↗
                    </a>
                    <Button variant="secondary" size="sm" onClick={() => setShowDocModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <ToastContainer
                position="top-center"
                autoClose={2500}
                hideProgressBar={true}
                type="success"
            />
        </div>
    );
};

export { ContributeInSahyogAPI };